/**
 * Who may change what in a mission by hand (release D, D11.3/D11.4): the
 * platform's own table (`EditRules.java`), copied to `../generated/edit-rules.json`
 * and kept equal by the platform's `EditRulesExportTest`, with the same three
 * predicates. The designer locks a control by it; the platform refuses the
 * same change if it arrives anyway.
 */
import table from '../generated/edit-rules.json';
import type { Definition } from '../api/missions';

export interface CollectionRule {
  id: string;
  scope: string;
  collection: string;
  match: string;
  editable: string[] | null;
  locked: string[] | null;
  deletable: boolean;
  addable: boolean;
  code: string;
  fixBy: string;
  reason: string;
}

export interface PathRule {
  id: string;
  scope: string;
  paths: string[];
  code: string;
  fixBy: string;
  reason: string;
}

export const EDIT_RULES = table as unknown as {
  collections: CollectionRule[];
  paths: PathRule[];
  skills: number[];
  ignored: string[];
};

type Entry = { id?: string; scene?: string; campaignRef?: string };

export function scripted(def: Definition): boolean {
  return !!def.script;
}

/** A script whose own clock stands the generic one down (R10). */
export function scriptedClock(def: Definition): boolean {
  const clock = (def.script as { clock?: { generic?: boolean } } | undefined)?.clock;
  return !!clock && !clock.generic;
}

function inScope(scope: string, def: Definition): boolean {
  if (scope === 'scripted') return scripted(def);
  if (scope === 'scriptedClock') return scriptedClock(def);
  if (scope === 'campaign') return !!(def as { campaign?: unknown }).campaign;
  // «Mou» generates the mission again, which a campaign's chapter cannot be: its points are dragged.
  if (scope === 'regenerable') return !(def as { campaign?: unknown }).campaign;
  return true;
}

/** An objective the script owns (`script.owns`). */
export function owned(def: Definition, objective: { id: string }): boolean {
  return ((def.script as { owns?: string[] } | undefined)?.owns ?? []).includes(objective.id);
}

export function inScene(entry: Entry): boolean {
  return typeof entry.scene === 'string' && entry.scene.trim() !== '';
}

function namedByScript(def: Definition): Set<string> {
  const out = new Set<string>();
  const walk = (n: unknown) => {
    if (typeof n === 'string') out.add(n.startsWith('force:') ? n.slice(6) : n);
    else if (Array.isArray(n)) n.forEach(walk);
    else if (n && typeof n === 'object') Object.values(n).forEach(walk);
  };
  walk(def.script);
  return out;
}

/** A force the script drives: in a scene, or named anywhere under `script`. */
export function scriptedForce(def: Definition, force: Entry): boolean {
  return inScene(force) || (!!force.id && namedByScript(def).has(force.id));
}

function matches(match: string, def: Definition, entry: Entry): boolean {
  switch (match) {
    case 'owned':
      return owned(def, { id: entry.id ?? '' });
    case 'scripted':
      return scriptedForce(def, entry);
    case 'scene':
      return inScene(entry);
    case 'campaignRef':
      return typeof entry.campaignRef === 'string' && entry.campaignRef !== '';
    default:
      return true;
  }
}

/**
 * The rule that holds an entry of a collection (`/objectives`, `/forces`…),
 * when one does: what of it the person may change, and why not the rest.
 */
export function ruleFor(def: Definition, collection: string, entry: Entry): CollectionRule | null {
  return (
    EDIT_RULES.collections.find(
      (r) => r.collection === collection && inScope(r.scope, def) && matches(r.match, def, entry),
    ) ?? null
  );
}

export function mayChange(rule: CollectionRule | null, field: string): boolean {
  if (!rule) return true;
  return rule.editable ? rule.editable.includes(field) : !(rule.locked ?? []).includes(field);
}

/** Whether a new entry may be added to a collection by hand. */
export function mayAdd(def: Definition, collection: string): boolean {
  return !EDIT_RULES.collections.some(
    (r) => r.collection === collection && r.match === 'all' && inScope(r.scope, def) && !r.addable,
  );
}

/** The rule that fixes a path (`/time/hour`), when one does. */
export function pathRule(def: Definition, path: string): PathRule | null {
  return (
    EDIT_RULES.paths.find(
      (r) =>
        inScope(r.scope, def) &&
        r.paths.some((p) => {
          const re = new RegExp('^' + p.replace(/\*/g, '[^/]+') + '(/|$)');
          return re.test(path);
        }),
    ) ?? null
  );
}
